import { useState, useEffect } from "react";
import { useAuth } from "@/contexts/AuthContext";
import { api } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Lock, User, Mail, Building2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

const ROLE_LABELS = {
  admin: "Administrator",
  hospitality_coordinator: "Hospitality Coordinator",
  sending_agency: "Sending Agency",
  department_head: "Department Head",
  cmo_delegate: "CMO / Delegate",
};

export default function ProfilePage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword !== confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    setSubmitting(true);
    try {
      await api.put("/auth/change-password", {
        current_password: currentPassword,
        new_password: newPassword,
      });
      toast.success("Password changed successfully");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to change password");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6 fade-in" data-testid="profile-page">
      <Button variant="ghost" onClick={() => navigate("/dashboard")} className="text-[#525C58] hover:text-[#1C1F1E]">
        <ArrowLeft className="h-4 w-4 mr-2" /> Back to Dashboard
      </Button>

      <h1 className="text-4xl font-semibold tracking-tight text-[#1C1F1E]" style={{ fontFamily: 'Manrope, sans-serif' }}>
        Profile
      </h1>

      <Card className="border-[#E8E8E6]">
        <CardHeader>
          <CardTitle className="text-lg" style={{ fontFamily: 'Manrope, sans-serif' }}>Account Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="flex items-center gap-3">
              <User className="h-5 w-5 text-[#7A8A84]" />
              <div>
                <p className="text-sm text-[#7A8A84]">Name</p>
                <p className="font-medium text-[#1C1F1E]" data-testid="profile-name">{user.name}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Mail className="h-5 w-5 text-[#7A8A84]" />
              <div>
                <p className="text-sm text-[#7A8A84]">Email</p>
                <p className="font-medium text-[#1C1F1E]" data-testid="profile-email">{user.email}</p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <Building2 className="h-5 w-5 text-[#7A8A84]" />
              <div>
                <p className="text-sm text-[#7A8A84]">Role</p>
                <Badge className="bg-[#1B4332] text-white border-0" data-testid="profile-role">
                  {ROLE_LABELS[user.role] || user.role}
                </Badge>
              </div>
            </div>
            {user.department && (
              <div className="flex items-center gap-3">
                <Building2 className="h-5 w-5 text-[#7A8A84]" />
                <div>
                  <p className="text-sm text-[#7A8A84]">Department</p>
                  <p className="font-medium text-[#1C1F1E]">{user.department}</p>
                </div>
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      <Card className="border-[#E8E8E6]">
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Lock className="h-5 w-5" /> Change Password
          </CardTitle>
          <CardDescription>Update your account password</CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleChangePassword} className="space-y-4" data-testid="change-password-form">
            <div className="space-y-2">
              <Label className="text-[#1C1F1E]">Current Password</Label>
              <Input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                required
                data-testid="current-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#1C1F1E]">New Password</Label>
              <Input
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                minLength={6}
                data-testid="new-password-input"
              />
            </div>
            <div className="space-y-2">
              <Label className="text-[#1C1F1E]">Confirm New Password</Label>
              <Input
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                data-testid="confirm-password-input"
              />
            </div>
            <Button
              type="submit"
              className="bg-[#1B4332] hover:bg-[#2D6A4F] transition-colors duration-200"
              disabled={submitting}
              data-testid="change-password-btn"
            >
              {submitting ? "Changing..." : "Change Password"}
            </Button>
          </form>
        </CardContent>
      </Card>

      <TwoFaCard />
    </div>
  );
}

function TwoFaCard() {
  const [status, setStatus] = useState(null);
  const [setup, setSetup] = useState(null);   // {qr_png, secret}
  const [code, setCode] = useState("");
  const [disablePwd, setDisablePwd] = useState("");
  const [busy, setBusy] = useState(false);

  const loadStatus = async () => {
    try { const { data } = await api.get("/auth/2fa/status"); setStatus(data); }
    catch { setStatus({ user_enabled: false, org_required: false }); }
  };
  useEffect(() => { loadStatus(); }, []);

  const startSetup = async () => {
    setBusy(true);
    try { const { data } = await api.post("/auth/2fa/setup"); setSetup(data); setCode(""); }
    catch (e) { toast.error(e.response?.data?.detail || "Setup failed"); }
    finally { setBusy(false); }
  };
  const enable = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/2fa/enable", { code });
      toast.success("Two-factor authentication is ON");
      setSetup(null); setCode("");
      loadStatus();
    } catch (er) { toast.error(er.response?.data?.detail || "Wrong code"); }
    finally { setBusy(false); }
  };
  const disable = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/auth/2fa/disable", { password: disablePwd });
      toast.success("Two-factor authentication turned off");
      setDisablePwd("");
      loadStatus();
    } catch (er) { toast.error(er.response?.data?.detail || "Failed"); }
    finally { setBusy(false); }
  };

  if (!status) return null;
  return (
    <Card className="border-[#E8E8E6]" data-testid="twofa-card">
      <CardHeader>
        <CardTitle className="text-lg flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
          <ShieldCheck className="h-5 w-5" /> Two-Factor Authentication
        </CardTitle>
        <CardDescription>
          Protect your account with a 6-digit code from Google Authenticator, Authy or any TOTP app.
          {status.org_required && " Your organisation requires 2FA."}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="flex items-center gap-2">
          <Badge className={status.user_enabled ? "bg-emerald-600 text-white border-0" : "bg-stone-200 text-stone-700 border-0"} data-testid="twofa-status-badge">
            {status.user_enabled ? "ENABLED" : "NOT SET UP"}
          </Badge>
          {status.user_enabled && <span className="text-xs text-[#7A8A84]">Trusted devices skip the code for 7 days</span>}
        </div>

        {!status.user_enabled && !setup && (
          <Button onClick={startSetup} disabled={busy} className="bg-[#1B4332] hover:bg-[#2D6A4F]" data-testid="twofa-setup-btn">
            <ShieldCheck className="h-4 w-4 mr-2" /> Set up authenticator
          </Button>
        )}

        {!status.user_enabled && setup && (
          <form onSubmit={enable} className="space-y-3" data-testid="twofa-enable-form">
            <p className="text-sm text-[#525C58]">1. Scan this QR with your authenticator app. 2. Enter the current 6-digit code.</p>
            <img src={setup.qr_png} alt="2FA QR" className="w-40 h-40 border border-[#E8E8E6] rounded-lg" data-testid="profile-twofa-qr" />
            <p className="text-[11px] text-[#7A8A84] font-mono break-all">Manual key: {setup.secret}</p>
            <div className="flex gap-2 items-center">
              <Input value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} maxLength={6}
                     placeholder="123456" className="w-36 text-center font-mono tracking-widest" data-testid="profile-twofa-code" />
              <Button type="submit" disabled={busy || code.length !== 6} className="bg-[#1B4332] hover:bg-[#2D6A4F]" data-testid="twofa-enable-btn">
                {busy ? "Checking…" : "Turn on 2FA"}
              </Button>
            </div>
          </form>
        )}

        {status.user_enabled && (
          <form onSubmit={disable} className="flex gap-2 items-center" data-testid="twofa-disable-form">
            <Input type="password" value={disablePwd} onChange={(e) => setDisablePwd(e.target.value)}
                   placeholder="Confirm password to turn off" className="w-64" data-testid="twofa-disable-password" />
            <Button type="submit" variant="outline" disabled={busy || !disablePwd}
                    className="border-red-300 text-red-700 hover:bg-red-50" data-testid="twofa-disable-btn">
              Turn off 2FA
            </Button>
          </form>
        )}
      </CardContent>
    </Card>
  );
}
